import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

interface CollapsibleSectionSignature {
  Element: HTMLDivElement;
  Args: {
    /** Header label shown on phone. */
    title: string;
    /** Optional count rendered next to the title (e.g. document count). */
    count?: number;
    /** False renders the body bare, no header, for content that must stay on screen on phone. */
    collapsible?: boolean;
    /** Pass with @onToggle to own the collapsed state, so something else can expand the section. */
    collapsed?: boolean;
    onToggle?: () => void;
  };
  Blocks: {
    default: [];
  };
}

// Phone-only: at `sm:` and up the header hides and the body always shows.
export default class CollapsibleSection extends Component<CollapsibleSectionSignature> {
  @tracked localCollapsed = true;

  get isCollapsible(): boolean {
    return this.args.collapsible ?? true;
  }

  get collapsed(): boolean {
    return this.args.collapsed ?? this.localCollapsed;
  }

  get bodyId(): string {
    return `collapsible-section-${guidFor(this)}`;
  }

  toggle = (): void => {
    if (this.args.onToggle) {
      this.args.onToggle();
    } else {
      this.localCollapsed = !this.localCollapsed;
    }
  };

  <template>
    {{#if this.isCollapsible}}
      <div ...attributes>
        <button
          type="button"
          class="flex sm:hidden w-full items-center justify-between gap-3 rounded-lg bg-carbon-5 px-4 py-3 text-left mb-4"
          aria-expanded={{if this.collapsed "false" "true"}}
          aria-controls={{this.bodyId}}
          data-test-collapsible-toggle
          {{on "click" this.toggle}}
        >
          <span class="font-bold text-carbon-90 text-sm">
            {{@title}}
            {{#if @count}}
              <span class="ml-2 font-normal text-carbon-50">{{@count}}</span>
            {{/if}}
          </span>
          <span class="shrink-0 transition-transform {{unless this.collapsed 'rotate-180'}}">
            {{svgJar "chevron-down-gray" width="12" height="7"}}
          </span>
        </button>

        <div id={{this.bodyId}} class={{if this.collapsed "hidden sm:block"}} data-test-collapsible-body>
          {{yield}}
        </div>
      </div>
    {{else}}
      <div ...attributes>
        {{yield}}
      </div>
    {{/if}}
  </template>
}
